import type { Locale } from "./i18n";

export type TagId = string;

export interface ScentTag {
  id: TagId;
  labels: Record<Locale, string>;
  emoji: string;
  /** Lowercased synonyms across all 3 languages incl. common typos & transliterations. */
  synonyms: string[];
}

/** Normalize a string for matching: lower-case + Arabic letter folding + diacritics removal. */
export function normalizeText(input: string): string {
  if (!input) return "";
  return input
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u064B-\u065F\u0670\u0640]/g, "")
    .replace(/[\u0622\u0623\u0625\u0671]/g, "\u0627")
    .replace(/[\u0649\u064A]/g, "\u064A")
    .replace(/\u0629/g, "\u0647")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * البذور الافتراضيّة (fallback). تُستبدَل في وقت التشغيل بقيم
 * جدول `scent_taxonomy_tags` عبر `applyTaxonomyOverrides()` من
 * `ScentTaxonomyLoader`. تبقى هنا كحارس SSR وحالة عدم اتصال بقاعدة البيانات.
 */
const SEED_TAGS: ScentTag[] = [
  { id: "oriental", labels: { en: "Oriental", ar: "شرقي", fr: "Oriental" }, emoji: "🌙", synonyms: ["oriental","orient","شرقي","شرقية","شرقيه","شرقى","oud","عود","عودي","amber","عنبر","امبر","أمبر","bakhoor","بخور","arabian","عربي","arabic"] },
  { id: "feminine", labels: { en: "Feminine", ar: "حريمي / نسائي", fr: "Féminin" }, emoji: "🌸", synonyms: ["feminine","femme","women","woman","for-her","forher","نسائي","نسائى","نسايي","حريمي","حرمي","حريمى","للنساء","floral","زهري","زهور","ورد","rose","romantic","رومانسي"] },
  { id: "masculine", labels: { en: "Masculine", ar: "رجالي", fr: "Masculin" }, emoji: "🖤", synonyms: ["masculine","men","man","homme","for-him","forhim","رجالي","رجالى","للرجال","ذكوري","leather","جلد","tobacco","تبغ"] },
  { id: "unisex", labels: { en: "Unisex", ar: "للجنسين", fr: "Mixte" }, emoji: "⚪️", synonyms: ["unisex","mixte","للجنسين","موحد","شبابي"] },
  { id: "floral", labels: { en: "Floral", ar: "زهري", fr: "Floral" }, emoji: "🌷", synonyms: ["floral","flower","flowers","fleur","زهري","زهرى","زهور","ورد","ياسمين","jasmine","rose","tuberose","gardenia","peony"] },
  { id: "woody", labels: { en: "Woody", ar: "خشبي", fr: "Boisé" }, emoji: "🌲", synonyms: ["woody","wood","boise","boisé","خشبي","خشبى","خشب","sandalwood","صندل","cedar","ارز","أرز","vetiver","فيتيفر"] },
  { id: "fresh", labels: { en: "Fresh", ar: "منعش", fr: "Frais" }, emoji: "💧", synonyms: ["fresh","frais","منعش","منعشة","منعشه","حمضي","حامض","ليموني","citrus","aquatic","mer","ocean","بحري","ماء","aqua"] },
  { id: "spicy", labels: { en: "Spicy", ar: "حار / توابل", fr: "Épicé" }, emoji: "🌶️", synonyms: ["spicy","spice","epice","épicé","حار","حاره","حارة","توابل","فلفل","pepper","cinnamon","قرفة","saffron","زعفران","cardamom","هيل"] },
  { id: "sweet", labels: { en: "Sweet", ar: "حلو", fr: "Sucré" }, emoji: "🍯", synonyms: ["sweet","sucre","sucré","حلو","حلوه","حلوة","gourmand","vanilla","فانيلا","فانيليا","caramel","كراميل","honey","عسل","chocolate","شوكولاته"] },
  { id: "gift", labels: { en: "Gift", ar: "هدية", fr: "Cadeau" }, emoji: "🎁", synonyms: ["gift","gifts","cadeau","هدية","هديه","هدايا","occasion","مناسبة","مناسبه","wrap","تغليف"] },
  { id: "daily", labels: { en: "Daily", ar: "يومي", fr: "Quotidien" }, emoji: "☀️", synonyms: ["daily","day","jour","يومي","يومى","نهاري","خفيف","light","office","عمل","دوام"] },
  { id: "evening", labels: { en: "Evening", ar: "سهرة", fr: "Soirée" }, emoji: "✨", synonyms: ["evening","night","soir","soiree","soirée","سهرة","سهره","ليلي","ليلى","مناسبات","party","حفلة","حفله","oud","intense","قوي"] },
];

const SEED_PRIMARY: TagId[] = ["oriental","feminine","masculine","fresh","sweet","gift","evening","daily"];
const SEED_FAMILY: TagId[] = ["oriental","floral","woody","fresh","spicy","sweet"];
const SEED_MOOD: TagId[] = ["feminine","masculine","unisex","daily","evening","gift"];

// ESM live bindings — importers always see the current value after override.
export let SCENT_TAGS: ScentTag[] = [...SEED_TAGS];
export let PRIMARY_MOOD_TAGS: TagId[] = [...SEED_PRIMARY];
export let FAMILY_TAGS: TagId[] = [...SEED_FAMILY];
export let MOOD_TAGS: TagId[] = [...SEED_MOOD];

let NORMALIZED_SYNONYMS: Array<{ tag: ScentTag; norm: string[] }> = SCENT_TAGS.map((t) => ({
  tag: t,
  norm: t.synonyms.map(normalizeText),
}));

export interface ScentTagOverride {
  id: string;
  label_ar: string;
  label_en: string;
  emoji: string;
  synonyms: string[];
  tag_group: "mood" | "family" | "both";
  sort_order: number;
  in_primary_mood: boolean;
}

const overrideListeners = new Set<() => void>();

export function onTaxonomyChange(cb: () => void): () => void {
  overrideListeners.add(cb);
  return () => overrideListeners.delete(cb);
}

/**
 * يُستدعى من ScentTaxonomyLoader عند جلب الأوسمة من Supabase.
 * يستبدل القوائم الافتراضيّة محليّاً ويُعيد بناء فهرس المرادفات.
 */
export function applyTaxonomyOverrides(rows: ScentTagOverride[]): void {
  if (!rows.length) return;
  const sorted = [...rows].sort((a, b) => a.sort_order - b.sort_order);
  SCENT_TAGS = sorted.map((r) => ({
    id: r.id,
    labels: { en: r.label_en, ar: r.label_ar, fr: r.label_en },
    emoji: r.emoji,
    synonyms: r.synonyms,
  }));
  PRIMARY_MOOD_TAGS = sorted.filter((r) => r.in_primary_mood).map((r) => r.id);
  FAMILY_TAGS = sorted.filter((r) => r.tag_group === "family" || r.tag_group === "both").map((r) => r.id);
  MOOD_TAGS = sorted.filter((r) => r.tag_group === "mood" || r.tag_group === "both").map((r) => r.id);
  NORMALIZED_SYNONYMS = SCENT_TAGS.map((t) => ({ tag: t, norm: t.synonyms.map(normalizeText) }));
  for (const cb of overrideListeners) cb();
}

/**
 * استنتاج أوسمة المنتج بمصدرَين منفصلَين (لمنع تضارب رجالي/نسائي معاً):
 * - أوسمة المزاج/الجنس/المناسبة (feminine/masculine/unisex/gift/daily/evening):
 *   تُطابَق فقط من إشارات صريحة → tags Shopify + productType + العنوان.
 * - أوسمة العائلة العطريّة (oriental/floral/woody/fresh/spicy/sweet):
 *   تُطابَق من نوتات العطر (قاعدتنا) إن وُجدت، وإلّا من الوصف.
 */
export function inferProductTags(input: {
  title?: string;
  description?: string;
  productType?: string;
  vendor?: string;
  tags?: string[];
  /** نوتات العطر (عليا/وسط/قاعدة) مجموعة في نصّ واحد — مصدر العائلة العطريّة. */
  notes?: string;
}): TagId[] {
  const explicit = normalizeText(
    [input.title, input.productType, input.vendor, ...(input.tags ?? [])].filter(Boolean).join(" "),
  );
  const content = normalizeText(input.notes?.trim() ? input.notes : (input.description ?? ""));
  if (!explicit && !content) return [];

  const moodOnly = new Set(MOOD_TAGS.filter((id) => !FAMILY_TAGS.includes(id)));
  // tags Shopify تُطابَق كـtokens كاملة كي لا يُلتقط "men" داخل "women".
  const explicitTokens = new Set(explicit.split(/[\s,\-_/]+/).filter(Boolean));
  const explicitCompact = explicit.replace(/[\s,\-_/]+/g, "");
  const found = new Set<TagId>();
  for (const { tag, norm } of NORMALIZED_SYNONYMS) {
    const isMood = moodOnly.has(tag.id);
    for (const syn of norm) {
      if (!syn) continue;
      const hit = isMood
        ? explicitTokens.has(syn) ||
          (syn.length >= 5 && explicitCompact.includes(syn.replace(/[\s\-_/]+/g, "")))
        : `${explicit} ${content}`.includes(syn);
      if (hit) {
        found.add(tag.id);
        break;
      }
    }
  }
  return Array.from(found);
}


/** Given a raw query, return tag ids whose synonyms match the query (used for query expansion). */
export function tagsMatchingQuery(query: string): TagId[] {
  const q = normalizeText(query);
  if (!q) return [];
  const out: TagId[] = [];
  for (const { tag, norm } of NORMALIZED_SYNONYMS) {
    if (norm.some((s) => s && (s.includes(q) || q.includes(s)))) {
      out.push(tag.id);
    }
  }
  return out;
}

/** Expand a search query with synonyms from matched tags (for fuzzy engines). */
export function expandQuery(query: string): string[] {
  const matched = tagsMatchingQuery(query);
  const expansions = new Set<string>([query]);
  for (const id of matched) {
    const tag = SCENT_TAGS.find((t) => t.id === id);
    if (tag) tag.synonyms.forEach((s) => expansions.add(s));
  }
  return Array.from(expansions);
}

export function getTag(id: TagId): ScentTag | undefined {
  return SCENT_TAGS.find((t) => t.id === id);
}
